import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchRequest as getSegmentDetail, clearState } from '../../redux/actions/segments/detail';
import { deleteSegmentRequest as deleteSegment } from '../../redux/actions/segments/delete';
import {
  previewSegmentByIdRequest,
  clearPreviewState,
} from '../../redux/actions/segments/preview';
import {
  Box, Typography, CircularProgress, Divider,
  Chip, Card, CardContent, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Stack, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import { SegmentsIcon } from '../global/CustomIcons';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format } from '../../utils/datetime';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import SegmentDataPreviewModal from '../common/SegmentDataPreviewModal';

// ---------------------------------------------------------------------------
// Strategy metadata - single source of truth, replaces the 3x copy-pasted
// switch chains the previous version had.
// ---------------------------------------------------------------------------
const STRATEGY_META = {
  nearest_value:        { name: 'Nearest Value',         desc: 'Uses the nearest value within the tolerance window' },
  previous_value:       { name: 'Previous Value',        desc: 'Uses the most recent previous value' },
  forward_value:        { name: 'Forward Value',         desc: 'Uses the next available value' },
  db_previous:          { name: 'Out of Range Previous', desc: 'Falls back to any previous signal, no tolerance limit' },
  db_nearest:           { name: 'Out of Range Nearest',  desc: 'Falls back to any nearest signal, no tolerance limit' },
  linear_interpolation: { name: 'Linear Interpolation',  desc: 'Uses linear interpolation between points (experimental)' },
  zero:                 { name: 'Zero',                  desc: 'Replaces missing values with zero' },
  mean:                 { name: 'Mean',                  desc: 'Replaces missing values with the segment mean' },
  median:               { name: 'Median',                desc: 'Replaces missing values with the segment median' },
  none:                 { name: 'None',                  desc: 'Leaves missing values as None/NaN' },
};
const strategyLabel = (id) => STRATEGY_META[id]?.name || id || 'Not specified';
const strategyDesc  = (id) => STRATEGY_META[id]?.desc || (id?.startsWith('custom:') ? `Custom literal: ${id.split(':', 2)[1]}` : 'Custom strategy');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDuration = (seconds) => {
  if (!seconds) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 && days === 0) parts.push(`${minutes}m`);
  return parts.join(' ') || `${seconds}s`;
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    if (typeof value === 'object' && value.$date) value = value.$date;
    if (value instanceof Date) return format(value, 'PPpp');
    return format(new Date(value), 'PPpp');
  } catch {
    return 'Invalid date';
  }
};

// ---------------------------------------------------------------------------
// Small presentational sub-components
// ---------------------------------------------------------------------------

// One labelled value - used everywhere a definition-list-ish row is needed.
const Field = ({ label, children, mono }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        wordBreak: 'break-all',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
        fontSize: mono ? '0.8rem' : undefined,
        color: 'text.primary',
        mt: 0.25,
      }}
    >
      {children ?? '—'}
    </Typography>
  </Box>
);

// Compact section card with a small heading.
//
// NOTE: ``sx`` is the named MUI prop, NOT a rest collector. The previous
// version used ``...sx`` which captured the prop as ``{ sx: {...} }`` and
// then spread it back into the Card's sx as a NESTED ``sx`` key - so any
// ``mb`` / ``mt`` overrides callers passed to <Section> were silently
// swallowed and the cards rendered flush against each other.
const Section = ({ title, action, children, sx }) => (
  <Card variant="outlined" sx={{ borderColor: 'divider', ...sx }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        {action}
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </CardContent>
  </Card>
);

// Metric tile - a labelled, accented numeric/string value with optional
// secondary annotation. Used to make Configuration's interval/tolerance/
// mode/etc. read at a glance instead of as small text rows.
const MetricTile = ({ label, value, unit, sub, accent, accentBg, icon }) => (
  <Box
    sx={{
      p: 1.75,
      borderRadius: 1,
      border: '1px solid',
      borderColor: accentBg ? 'transparent' : 'divider',
      bgcolor: accentBg || 'transparent',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
      {icon}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', fontWeight: 500 }}
      >
        {label}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, lineHeight: 1.1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: accent || 'text.primary', lineHeight: 1.1 }}>
        {value}
      </Typography>
      {unit && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {unit}
        </Typography>
      )}
    </Box>
    {sub && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// Visual time-range bar with start/end labels and a live "now" pulse.
const TimeRangeBar = ({ start, end, durationSeconds, live }) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', fontWeight: 500 }}
      >
        Time Range
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {durationSeconds ? formatDuration(durationSeconds) : '—'}
      </Typography>
    </Box>

    {/* The bar itself - solid fill for historical, gradient + pulse for live */}
    <Box
      sx={{
        position: 'relative',
        height: 8,
        bgcolor: 'rgba(97, 77, 238, 0.1)',
        borderRadius: 4,
        mb: 1.5,
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: live
            ? 'linear-gradient(90deg, rgba(97, 77, 238, 0.7), rgba(46, 125, 50, 0.85))'
            : 'rgba(97, 77, 238, 0.6)',
          borderRadius: 4,
        }}
      />
      {live && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translate(50%, -50%)',
            width: 12,
            height: 12,
            bgcolor: '#2e7d32',
            borderRadius: '50%',
            boxShadow: '0 0 0 4px rgba(46, 125, 50, 0.25)',
            '@keyframes pulse': {
              '0%, 100%': { boxShadow: '0 0 0 4px rgba(46, 125, 50, 0.25)' },
              '50%':      { boxShadow: '0 0 0 9px rgba(46, 125, 50, 0)' },
            },
            animation: 'pulse 2s infinite ease-in-out',
          }}
        />
      )}
    </Box>

    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {start ? formatDate(start) : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">Start</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: live ? '#2e7d32' : 'text.primary' }}
        >
          {live ? 'Now (live)' : (end ? formatDate(end) : '—')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          End{live ? ' (follows current time)' : ''}
        </Typography>
      </Box>
    </Box>
  </Box>
);

// One strategy "step" rendered as a small inline pill + description tooltip.
const StrategyPill = ({ id, index, color = 'primary.main', bg = 'rgba(97, 77, 238, 0.08)' }) => (
  <Tooltip title={strategyDesc(id)} arrow placement="top">
    <Chip
      size="small"
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          {typeof index === 'number' && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16, height: 16, borderRadius: '50%',
                bgcolor: color, color: 'white',
                fontSize: '0.65rem', fontWeight: 600,
              }}
            >
              {index}
            </Box>
          )}
          <span>{strategyLabel(id)}</span>
        </Box>
      }
      sx={{
        bgcolor: bg,
        color,
        border: `1px solid ${typeof color === 'string' ? color : 'rgba(0,0,0,0.1)'}`,
        borderColor: 'rgba(97, 77, 238, 0.25)',
        fontWeight: 500,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  </Tooltip>
);

// Render a chain (primary + fallbacks) as a horizontal sequence of pills with
// "→" separators - works for both global and field-specific strategies.
const ChainRow = ({ primary, fallbacks = [], color, bg }) => {
  const all = [primary, ...(fallbacks || [])].filter(Boolean);
  if (all.length === 0) return <Typography variant="body2" color="text.secondary">No strategy defined</Typography>;
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
      {all.map((s, i) => (
        <React.Fragment key={`${s}-${i}`}>
          {i > 0 && (
            <Typography component="span" variant="caption" color="text.disabled" sx={{ mx: 0.25 }}>→</Typography>
          )}
          <StrategyPill id={s} index={i + 1} color={color} bg={bg} />
        </React.Fragment>
      ))}
    </Stack>
  );
};

// Compact chip for a feature/label/EF name.
//
// ``marketplace`` (optional) is the per-signal block emitted by the
// BE for actively-subscribed marketplace signals. When present the
// chip switches to a marketplace tone and shows a Storefront glyph.
//
// ``missing`` (optional) marks a signal whose marketplace
// subscription is no longer active (or whose listing was delisted).
// The chip flips to a warning tone with a strike-through name and a
// tooltip that explains why the signal will be dropped at training /
// inference time. Visual is intentionally loud since the rest of the
// page is muted.
/* Tier → colour for the confidence dot. Mirrors the Predictive Influence
   panel so the language stays consistent across the product. */
const TIER_DOT = {
  validated: '#2e7d32',
  suggestive: '#ed6c02',
  candidate: '#0288d1',
  leading: '#7b1fa2',
  manual: '#9e9e9e',
};

const FieldChip = ({ name, type, kind, marketplace, missing, confidence, impact, tier }) => {
  // kind: 'feature' | 'label' | 'engineered'
  const palette = {
    feature:    { bg: 'rgba(97, 77, 238, 0.08)',  color: '#614dee', border: 'rgba(97, 77, 238, 0.25)' },
    label:      { bg: 'rgba(46, 125, 50, 0.08)',  color: '#2e7d32', border: 'rgba(46, 125, 50, 0.25)' },
    engineered: { bg: 'rgba(255, 152, 0, 0.08)',  color: '#ed6c02', border: 'rgba(255, 152, 0, 0.3)'  },
  }[kind] || { bg: 'rgba(0,0,0,0.04)', color: 'text.primary', border: 'divider' };

  // Override palette when the signal is missing (subscription lapsed).
  if (missing) {
    palette.bg     = 'rgba(217, 119, 6, 0.10)';   // amber 600 @ 10%
    palette.color  = '#B45309';                   // amber 700
    palette.border = 'rgba(217, 119, 6, 0.45)';
  }

  // Pretty display label: marketplace aliases (`mkt:lst_ABC:wind`)
  // are noisy - show the listing title + original key when we have it.
  let display = name;
  if (marketplace) {
    const orig = name.split(':').slice(2).join(':');
    display = marketplace.listing_title
      ? `${marketplace.listing_title} · ${orig}`
      : orig;
  } else if (missing) {
    const orig = name.startsWith('mkt:')
      ? name.split(':').slice(2).join(':')
      : name;
    display = orig;
  }

  const hasBelief = typeof confidence === 'number' && Number.isFinite(confidence);
  const dotColor = TIER_DOT[tier] || TIER_DOT.manual;
  const beliefLine = hasBelief
    ? ` · confidence ${(confidence * 100).toFixed(0)}%${
      typeof impact === 'number' && Number.isFinite(impact)
        ? ` · impact ${(impact * 100).toFixed(0)}%` : ''}${tier ? ` · ${tier}` : ''}`
    : '';
  const tooltip = missing
    ? `Missing data — your marketplace subscription for "${display}" is no longer active. This signal is skipped during training and inference until you resubscribe.`
    : marketplace
      ? `Marketplace · ${marketplace.seller_label || 'Seller'}${type ? ` · ${type}` : ''}${beliefLine}`
      : (type ? `${name} (${type})${beliefLine}` : `${name}${beliefLine}`);

  let icon;
  if (missing) {
    icon = <WarningAmberIcon sx={{ fontSize: '0.85rem !important', color: `${palette.color} !important` }} />;
  } else if (marketplace) {
    icon = <StorefrontIcon sx={{ fontSize: '0.85rem !important', color: `${palette.color} !important` }} />;
  } else if (kind === 'engineered') {
    icon = <AutoAwesomeIcon sx={{ fontSize: '0.85rem !important', color: `${palette.color} !important` }} />;
  }

  // A confidence dot (when belief is present and no other icon claims the
  // slot) so the trust tier reads at a glance without crowding the chip.
  const beliefIcon = (!icon && hasBelief)
    ? (
      <Box
        component="span"
        sx={{
          width: 8, height: 8, borderRadius: '50%', ml: 0.75,
          flexShrink: 0, bgcolor: dotColor,
          // Confidence drives the fill opacity so weak signals look faint.
          opacity: 0.35 + 0.65 * Math.max(0, Math.min(1, confidence)),
        }}
      />
    )
    : icon;

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Chip
        size="small"
        icon={beliefIcon}
        label={display}
        sx={{
          bgcolor: palette.bg,
          color: palette.color,
          border: `1px solid ${palette.border}`,
          fontSize: '0.72rem',
          height: 24,
          textDecoration: missing ? 'line-through' : 'none',
          opacity: missing ? 0.85 : 1,
          '& .MuiChip-label': { px: 1 },
          '& .MuiChip-icon': { ml: 0.5 },
        }}
      />
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const SegmentDetailPage = ({
  getSegmentDetail, clearState, segment, isLoading, error,
  deleteSegment,
  previewSegmentById, clearPreviewState,
  previewData, previewLoading, previewError,
}) => {
  const { segmentId } = useParams();
  const navigate = useNavigate();
  const [workspaceSelected, setWorkspaceSelected] = useState(true);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(100);

  useEffect(() => {
    const workspaceId = localStorage.getItem('WORKSPACE_ID');
    if (!workspaceId) {
      setWorkspaceSelected(false);
      return;
    }
    if (segmentId) getSegmentDetail(segmentId);
    return () => {
      clearState();
      clearPreviewState();
    };
  }, [getSegmentDetail, clearState, clearPreviewState, segmentId]);

  // ----- preview handlers ----------------------------------------------------
  const handleOpenPreviewModal = () => {
    // Snap to page 1 so re-opening the preview doesn't carry a
    // stale page index from a previous session (the BE clamps too,
    // but resetting locally keeps the first render correct).
    setPreviewPage(1);
    setPreviewModalOpen(true);
    previewSegmentById({ segment_id: segmentId, page: 1, page_size: previewPageSize });
  };
  const handleClosePreviewModal = () => setPreviewModalOpen(false);
  const handlePreviewPageChange = (newPage) => {
    setPreviewPage(newPage);
    previewSegmentById({ segment_id: segmentId, page: newPage, page_size: previewPageSize });
  };
  const handlePreviewRowsPerPageChange = (newPageSize) => {
    setPreviewPageSize(newPageSize);
    setPreviewPage(1);
    previewSegmentById({ segment_id: segmentId, page: 1, page_size: newPageSize });
  };

  // Sync local pagination to whatever the BE actually returned -
  // it clamps out-of-range page numbers, so without this hook the
  // local state would still show the stale (non-existent) page
  // index after the clamp.
  useEffect(() => {
    const pag = previewData?.pagination;
    if (!pag) return;
    const current = Number(pag.current_page);
    const requested = Number(pag.requested_page ?? pag.current_page);
    if (
      Number.isFinite(current)
      && Number.isFinite(requested)
      && current !== requested
      && current !== previewPage
    ) {
      setPreviewPage(current);
    }
  }, [previewData, previewPage]);

  // ----- delete handlers ----------------------------------------------------
  const handleConfirmDelete = () => {
    setIsDeleting(true);
    deleteSegment(segmentId);
    setTimeout(() => navigate('/segments'), 1000);
  };

  // ----- guards -------------------------------------------------------------
  if (!workspaceSelected) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <ErrorIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" gutterBottom>No workspace selected</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Pick a workspace from the header to view segment details.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/workspaces')} sx={{ mt: 1 }}>
          Go to workspaces
        </Button>
      </Box>
    );
  }
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>Couldn't load segment</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{error}</Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/segments')}>
          Back to Segments
        </Button>
      </Box>
    );
  }
  if (!segment) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Segment not found</Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/segments')} sx={{ mt: 2 }}>
          Back to Segments
        </Button>
      </Box>
    );
  }

  // ----- derived display data ----------------------------------------------
  const features            = segment.fields?.features || [];
  const labels              = segment.fields?.labels || [];
  const engineeredFeatures  = segment.fields?.engineered_features || [];
  const { interval = 0, tolerance = 0, live = false, tail_seconds = 0 } = segment.configuration || {};
  const { global_strategy: norm = {}, field_strategies: fieldStrategies = {} } = segment.normalization || {};
  const fieldStrategyEntries = Object.entries(fieldStrategies || {});

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Delete dialog ------------------------------------------------------ */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Delete segment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{segment?.name ? ` "${segment.name}"` : ' this segment'}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => setIsDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={handleConfirmDelete} disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header ------------------------------------------------------------- */}
      <PageBreadcrumbs
        items={[
          { text: 'Segments', icon: <SegmentsIcon />, link: '/segments' },
          { text: segment.name || segment.uid },
        ]}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {segment.name || 'Untitled segment'}
          </Typography>
          <Chip
            size="small"
            icon={<FiberManualRecordIcon sx={{ fontSize: '0.7rem !important', color: live ? '#2e7d32 !important' : '#9e9e9e !important' }} />}
            label={live ? 'Real Time' : 'Historical'}
            sx={{
              fontWeight: 500,
              bgcolor: live ? 'rgba(46, 125, 50, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              color: live ? '#2e7d32' : 'text.secondary',
              border: `1px solid ${live ? 'rgba(46, 125, 50, 0.25)' : 'rgba(0,0,0,0.1)'}`,
              '& .MuiChip-icon': { ml: 0.75 },
            }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained" color="primary" size="small"
            startIcon={<TableRowsIcon />} onClick={handleOpenPreviewModal}
          >
            Preview Data
          </Button>
          <Button
            variant="outlined" color="error" size="small"
            startIcon={<DeleteIcon />} onClick={() => setIsDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Stack>
      </Box>

      {/* Overview - compact horizontal field grid (full width) -------------- */}
      <Section title="Overview" sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 2.5,
          }}
        >
          <Field label="ID" mono>{segment.id || segment.uid}</Field>
          <Field label="Created">{formatDate(segment.created_at)}</Field>
          <Field label="Tail Window">{formatDuration(tail_seconds)}</Field>
          {segment.description && <Field label="Description">{segment.description}</Field>}
          {segment.purpose && <Field label="Purpose">{segment.purpose}</Field>}
        </Box>
      </Section>

      {/* Configuration - visual: metric tiles + time-range bar -------------- */}
      <Section title="Configuration" sx={{ mb: 2.5 }}>
        {/* Tiles: Interval / Tolerance / Tail / Mode */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 2,
            mb: segment.data_summary?.time_range ? 3 : 0,
          }}
        >
          <MetricTile
            label="Interval"
            value={interval}
            unit="seconds"
            sub={`one bucket every ${formatDuration(interval)}`}
          />
          <MetricTile
            label="Tolerance"
            value={tolerance}
            unit="seconds"
            sub={`±${formatDuration(tolerance)} per bucket`}
          />
          <MetricTile
            label="Tail Window"
            value={formatDuration(tail_seconds) || '—'}
            sub={tail_seconds ? `${tail_seconds.toLocaleString()}s` : undefined}
          />
          <MetricTile
            label="Mode"
            value={live ? 'Real Time' : 'Historical'}
            accent={live ? '#2e7d32' : 'text.primary'}
            accentBg={live ? 'rgba(46, 125, 50, 0.06)' : undefined}
            icon={
              <FiberManualRecordIcon
                sx={{ fontSize: '0.7rem', color: live ? '#2e7d32' : '#9e9e9e' }}
              />
            }
            sub={live ? 'end follows current time' : 'fixed time window'}
          />
        </Box>

        {/* Visual time range */}
        {segment.data_summary?.time_range && (
          <TimeRangeBar
            start={segment.data_summary.time_range.start}
            end={segment.data_summary.time_range.end}
            durationSeconds={segment.data_summary.time_range.duration_seconds}
            live={live}
          />
        )}
      </Section>

      {/* Discovered channels (plan Step 9b): segments assembled by
          discovery carry labeled channel groups with validated evidence
          per entry. Hand-built segments don't have this block. */}
      {segment.channel_groups
        && Object.values(segment.channel_groups).some((g) => (g || []).length > 0) && (
        <Section
          title="Discovered channels"
          action={
            <Typography variant="caption" color="text.secondary">
              assembled by discovery — every entry carries its validated evidence
            </Typography>
          }
          sx={{ mb: 2.5 }}
        >
          <Stack spacing={2.5}>
            {[
              ['signals', 'Signals'],
              ['user_features', 'Your features'],
              ['event_channels', 'Event channels'],
              ['generated', 'Generated'],
            ].map(([groupKey, groupLabel]) => {
              const entries = segment.channel_groups?.[groupKey] || [];
              if (!entries.length) return null;
              return (
                <Box key={groupKey}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                    {groupLabel} ({entries.length})
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                    {entries.map((entry, i) => (
                      <Tooltip
                        key={i}
                        arrow placement="top"
                        title={
                          <Box sx={{ fontSize: 11 }}>
                            <Box><strong>{entry.display || entry.name}</strong></Box>
                            {groupKey === 'event_channels' && entry.key && (
                              <Box>derived from event stream "{entry.key}"</Box>
                            )}
                            {groupKey === 'user_features' && entry.segment_name && (
                              <Box>from your segment "{entry.segment_name}"</Box>
                            )}
                            {entry.evidence && (
                              <Box>
                                validated lift {entry.evidence.lift_pct > 0 ? '+' : ''}
                                {entry.evidence.lift_pct}%
                                {entry.evidence.lag_bins > 0 ? `, leads by ${entry.evidence.lag_bins} steps` : ''}
                              </Box>
                            )}
                          </Box>
                        }
                      >
                        <Chip
                          size="small"
                          label={entry.display || entry.name}
                          sx={{
                            bgcolor: groupKey === 'event_channels'
                              ? 'rgba(156, 39, 176, 0.08)'
                              : groupKey === 'user_features'
                                ? 'rgba(255, 152, 0, 0.08)'
                                : 'rgba(25, 118, 210, 0.08)',
                            fontSize: '0.72rem',
                            height: 24,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Section>
      )}

      {/* Fields - one card, three sub-rows. Replaces the broken arrow viz. -- */}
      <Section
        title="Fields"
        action={
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {features.length} features · {labels.length} labels · {engineeredFeatures.length} engineered
            </Typography>
          </Stack>
        }
        sx={{ mb: 2.5 }}
      >
        {/* Marketplace warning banner — surfaces any
            ``mkt:<lid>:<key>`` features whose subscription is no
            longer active. The BE has already excluded them from the
            data plane (training / inference / preview), so this is
            the only place a buyer learns the model output is now
            missing those columns until they resubscribe. */}
        {(segment.marketplace?.missing_signals || []).length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2.5, borderRadius: 1.5 }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 13.5, mb: 0.25 }}>
              {segment.marketplace.missing_signals.length} marketplace
              signal{segment.marketplace.missing_signals.length === 1 ? '' : 's'}
              {' '}missing
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Their subscription was cancelled or the listing was delisted.
              They are skipped during training and inference and won't
              appear in segment data — resubscribe in the marketplace to
              restore them:{' '}
              {segment.marketplace.missing_signals
                .map((m) => m.listing_title || m.public_key)
                .join(', ')}.
            </Typography>
          </Alert>
        )}

        <Stack spacing={2.5}>
          {features.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                Features ({features.length})
                {features.some((f) => typeof f.confidence === 'number') && (
                  <Box component="span" sx={{ ml: 1, textTransform: 'none', letterSpacing: 0, fontSize: '0.65rem', opacity: 0.8 }}>
                    — dot = model confidence (hover for impact &amp; tier)
                  </Box>
                )}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                {features.map((f, i) => (
                  <FieldChip
                    key={i}
                    name={f.name}
                    type={f.type}
                    kind="feature"
                    marketplace={f.marketplace}
                    missing={f.missing}
                    confidence={f.confidence}
                    impact={f.impact}
                    tier={f.tier}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {labels.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                Labels ({labels.length})
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                {labels.map((l, i) => (
                  <FieldChip
                    key={i}
                    name={l.name}
                    type={l.type}
                    kind="label"
                    marketplace={l.marketplace}
                    missing={l.missing}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {engineeredFeatures.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                Engineered features ({engineeredFeatures.length})
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                {engineeredFeatures.map((ef, i) => (
                  <Tooltip
                    key={i}
                    arrow placement="top"
                    title={
                      <Box sx={{ fontSize: 11 }}>
                        <Box><strong>{ef.name}</strong></Box>
                        <Box>{ef.technique_name || ef.technique}</Box>
                        {ef.source_field && <Box>source: {ef.source_field}</Box>}
                        {ef.category && <Box>category: {ef.category}</Box>}
                        {typeof ef.confidence === 'number' && Number.isFinite(ef.confidence) && (
                          <Box>
                            confidence: {(ef.confidence * 100).toFixed(0)}%
                            {typeof ef.impact === 'number' && Number.isFinite(ef.impact)
                              ? ` · impact: ${(ef.impact * 100).toFixed(0)}%` : ''}
                            {ef.tier ? ` · ${ef.tier}` : ''}
                          </Box>
                        )}
                      </Box>
                    }
                  >
                    <Chip
                      size="small"
                      icon={<AutoAwesomeIcon sx={{ fontSize: '0.85rem !important', color: '#ed6c02 !important' }} />}
                      label={ef.name}
                      sx={{
                        bgcolor: 'rgba(255, 152, 0, 0.08)',
                        color: '#ed6c02',
                        border: '1px solid rgba(255, 152, 0, 0.3)',
                        fontSize: '0.72rem',
                        height: 24,
                        '& .MuiChip-label': { px: 1 },
                        '& .MuiChip-icon': { ml: 0.5 },
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Section>

      {/* Normalization - global + field-specific in one card ---------------- */}
      <Section title="Normalization">
        <Stack spacing={2.5}>
          {/* Global */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                Global strategy
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tolerance: {norm?.tolerance || tolerance}s
              </Typography>
            </Box>
            <ChainRow primary={norm?.primary_strategy} fallbacks={norm?.fallback_strategies} />
          </Box>

          <Divider />

          {/* Field-specific */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
              Field-specific overrides ({fieldStrategyEntries.length})
            </Typography>
            {fieldStrategyEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None — all fields use the global strategy.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {fieldStrategyEntries.map(([fieldName, s]) => (
                  <Box
                    key={fieldName}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 1fr) 3fr auto' },
                      gap: { xs: 1, sm: 2 },
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {fieldName}
                    </Typography>
                    <ChainRow
                      primary={s?.primary_strategy}
                      fallbacks={s?.fallback_strategies}
                      color="#1976d2"
                      bg="rgba(25, 118, 210, 0.08)"
                    />
                    <Typography variant="caption" color="text.secondary">
                      tol: {s?.tolerance ?? norm?.tolerance ?? tolerance}s
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Alert severity="info" variant="outlined" sx={{ py: 0.5, fontSize: '0.8rem', '& .MuiAlert-message': { py: 0.5 } }}>
            Strategies are applied in order. Hover any pill to see what it does.
            Field-specific overrides take precedence over the global chain.
          </Alert>
        </Stack>
      </Section>

      {/* Preview modal ------------------------------------------------------ */}
      <SegmentDataPreviewModal
        open={previewModalOpen}
        handleClose={handleClosePreviewModal}
        data={previewData}
        loading={previewLoading}
        error={previewError}
        onPageChange={handlePreviewPageChange}
        onRowsPerPageChange={handlePreviewRowsPerPageChange}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  segment:        state.segment_detail.data,
  isLoading:      state.segment_detail.loading,
  error:          state.segment_detail.error,
  deleteError:    state.segment_delete?.error,
  previewData:    state.segment_preview?.response,
  previewLoading: state.segment_preview?.isLoading,
  previewError:   state.segment_preview?.error,
});

const mapDispatchToProps = {
  getSegmentDetail,
  clearState,
  deleteSegment,
  previewSegmentById: previewSegmentByIdRequest,
  clearPreviewState,
};

export default connect(mapStateToProps, mapDispatchToProps)(SegmentDetailPage);
